import React, { createContext, useContext } from "react";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import { SOCIAL_PLATFORMS } from "repoV2/utils/social/constants";
import { getDummyImageSrc } from "repoV2/utils/common/render/images";
import { checkIsEmptyHtmlString } from "repoV2/utils/common/validators/validators";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import { getPreviewableValue } from "repoV2/utils/previewPages";
import { useSalePageContext } from "../SalePageContext";

type IAboutCreatorSectionContextValue = {
    creatorName: string;
    shortDescription: string;
    profileImgSrc: string;
    hideProfileImg: boolean;
    description: string;
    hideSocialStatsList: boolean;
    sliderImgList: Array<{ title: string; media_logo: string }>;
    socialStatsList: Array<{
        key: string;
        imgSrc: string;
        onClick: () => void;
        followerCount?: number;
        subText: string;
    }>;
    hide: boolean;
};

const initialContextValue: IAboutCreatorSectionContextValue = {
    creatorName: "",
    shortDescription: "",
    profileImgSrc: "",
    hideProfileImg: false,
    description: "",
    hideSocialStatsList: false,
    socialStatsList: [],
    hide: false,
    sliderImgList: [],
};
const dummyData: IAboutCreatorSectionContextValue["sliderImgList"] = Array.from(
    Array(3)
).map((x: any, i: number) => {
    const idx = i + 1;
    return {
        title: `&lt;slider Image ${idx}&gt;`,
        media_logo: getDummyImageSrc({
            width: 205,
            height: 128,
            text: `slider Image ${idx}`,
        }),
    };
});

const dummyProfileImgSrc = getDummyImageSrc({ width: 500, height: 500 });

const AboutCreatorSectionContext = createContext(initialContextValue);

export function AboutCreatorSectionContextProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { basicData, sectionsData, isPreview } = useSalePageContext();
    const sliderImgList: IAboutCreatorSectionContextValue["sliderImgList"] =
        (isPreview &&
        checkIsArrayEmpty(sectionsData.aboutCreatorSection.sliderImgList)
            ? dummyData
            : sectionsData.aboutCreatorSection.sliderImgList) || [];

    const socialStatsList: IAboutCreatorSectionContextValue["socialStatsList"] =
        Object.values(SOCIAL_PLATFORMS)
            .filter(i => !!basicData.aboutCreator.socialLinks[i.key])
            .map(i => ({
                key: i.key,
                imgSrc: i.icon,
                onClick: () => {
                    window.open(
                        basicData.aboutCreator.socialLinks[i.key] || "",
                        "_blank"
                    );
                },
                followerCount: basicData.aboutCreator.followerCount[i.key],
                subText: SOCIAL_PLATFORMS[i.key].subtitle,
            }));

    const creatorName = getPreviewableValue({
        value: sectionsData.aboutCreatorSection.title,
        dummyString: "&lt;About the creator&gt",
        isPreview,
    });

    const shortDescription = getPreviewableValue({
        value: sectionsData.aboutCreatorSection.shortDescription,
        dummyString: "&lt;headline&gt;",
        isPreview,
    });
    const profileImgSrc =
        sectionsData.aboutCreatorSection.creatorImg ||
        (isPreview ? dummyProfileImgSrc : "");
    const hideProfileImg = checkIsStringEmpty(profileImgSrc);

    const description = getPreviewableValue({
        value: sectionsData.aboutCreatorSection.description,
        dummyString: "&lt;description&gt;",
        isPreview,
    });

    const contextValue: IAboutCreatorSectionContextValue = {
        creatorName,
        shortDescription,
        profileImgSrc,
        description,
        hideSocialStatsList: checkIsArrayEmpty(socialStatsList),
        hideProfileImg,
        socialStatsList,
        sliderImgList,
        hide:
            checkIsEmptyHtmlString(creatorName) &&
            checkIsEmptyHtmlString(shortDescription) &&
            hideProfileImg &&
            checkIsEmptyHtmlString(description),
    };
    return (
        <AboutCreatorSectionContext.Provider value={contextValue}>
            {children}
        </AboutCreatorSectionContext.Provider>
    );
}

export function useAboutCreatorSectionContext() {
    return useContext(AboutCreatorSectionContext);
}
